import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import { compose, map, prop, sortBy, toLower } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import {
  useOrganizationApplicationDelete,
  useOrganizationApplications,
} from "../organizationServices";
import YesNoCard from "../../components/YesNoCard";
import EditIcon from "@mui/icons-material/Edit";
import ApplicationAccessDialog from "../edit/ApplicationAccessDialog";
import { AlertContext } from "../../components/alert/AlertProvider";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 100,
  },
}));

const OrganizationApplicationsCard = ({ id }) => {
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);
  const [applications, setApplications] = useState([]);

  const appsResponse = useOrganizationApplications(id);
  useEffect(() => {
    if (appsResponse.isSuccess) {
      setApplications(
        sortBy(compose(toLower, prop("dateCreated")), appsResponse.data),
      );
    }
  }, [appsResponse.isSuccess, appsResponse.data]);

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setSelectedApplication(null);
    setDialogOpen(true);
  }, []);

  const handleEditClick = useCallback(applicationId => {
    setSelectedApplication(applicationId);
    setDialogOpen(true);
  }, []);

  const handleApplicationAccessCancel = useCallback(() => {
    setSelectedApplication(null);
    setDialogOpen(false);
  }, []);

  const handleApplicationAccessSave = useCallback(
    async data => {
      await appsResponse.refetch();
      setDialogOpen(false);
      setSelectedApplication(null);
    },
    [appsResponse],
  );

  const applicationAccessDelete = useOrganizationApplicationDelete();
  const handleDeleteClick = useCallback(
    async applicationId => {
      try {
        await applicationAccessDelete.mutateAsync({
          resourceIds: {
            orgId: id,
            appId: applicationId,
          },
        });
        await appsResponse.refetch();
      } catch (err) {
        alertError(err);
      }
    },
    [id, applicationAccessDelete, appsResponse, alertError],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Applications"
        action={
          <Tooltip title="Add Application">
            <IconButton onClick={handleAddClick} size="large">
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {appsResponse.isLoading && <PanelLoadingIndicator />}
        {appsResponse.isSuccess && isBlank(applications) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Applications set up for this Organization.
          </Box>
        )}
        {appsResponse.isSuccess && notBlank(applications) && (
          <TableContainer>
            <Table stickyHeader aria-label="Applications">
              <TableHead>
                <TableRow>
                  <TableCell
                    key="application-name"
                    align="left"
                    className={classes.cell}
                  >
                    Name
                  </TableCell>

                  <TableCell
                    key="restricted"
                    align="center"
                    className={classes.cell}
                  >
                    Restricted
                  </TableCell>

                  <TableCell
                    key="active"
                    align="center"
                    className={classes.cell}
                  >
                    Active
                  </TableCell>

                  <TableCell
                    key="granted-on"
                    align="left"
                    className={classes.cell}
                  >
                    Granted On
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 100 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(
                  app => (
                    <TableRow hover tabIndex={-1} key={app.applicationId}>
                      <TableCell
                        key={"application-name:" + app.applicationId}
                        align="left"
                        className={classes.cell}
                      >
                        {app.applicationName}
                      </TableCell>

                      <TableCell
                        key={"restricted:" + app.applicationId}
                        align="center"
                        className={classes.cell}
                      >
                        <YesNoCard
                          value={app.accessRestricted}
                          showNoIcon={false}
                        />
                      </TableCell>

                      <TableCell
                        key={"active:" + app.applicationId}
                        align="center"
                        className={classes.cell}
                      >
                        <YesNoCard value={app.active} showNoIcon={false} />
                      </TableCell>

                      <TableCell
                        key={"granted-on:" + app.applicationId}
                        align="left"
                        className={classes.cell}
                      >
                        {new Date(app.dateCreated).toLocaleDateString()}
                      </TableCell>

                      <TableCell
                        key={"actions:" + app.applicationId}
                        align="center"
                        style={{ width: 100 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          pl={4}
                        >
                          <Tooltip title="Edit">
                            <IconButton
                              classes={{ label: classes.icon }}
                              onClick={() => handleEditClick(app.applicationId)}
                              size="large"
                            >
                              <EditIcon color="primary" fontSize="large" />
                            </IconButton>
                          </Tooltip>
                          <ConfirmButton
                            handleDelete={() =>
                              handleDeleteClick(app.applicationId)
                            }
                            dialogTitle="Remove Application"
                            dialogBody="Are you sure you want to remove this application?"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ),
                  applications,
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      {dialogOpen && (
        <ApplicationAccessDialog
          open={dialogOpen}
          organizationId={id}
          applicationId={selectedApplication}
          onCancel={handleApplicationAccessCancel}
          onSave={handleApplicationAccessSave}
        />
      )}
    </Card>
  );
};

export default OrganizationApplicationsCard;
